import { useMemo } from 'react';
import { CHART, CSS_VARIABLES, CHART_COLORS } from '../constants';

interface UsePriceChartStylesProps {
  isNegativeMovement: boolean;
  isLoading: boolean;
  hasStuckTooltip: boolean;
}

/**
 * Use a css variable as a css variable
 */
export const getChartCssVar = (variable: keyof typeof CSS_VARIABLES) =>
  `var(${CSS_VARIABLES[variable]})`;

/**
 * Generate CSS variables object for chart styling
 */
const getCssVariables = ({
  isNegativeMovement,
  hasStuckTooltip,
}: {
  isNegativeMovement: boolean;
  hasStuckTooltip: boolean;
}) => ({
  [CSS_VARIABLES.PRICE_COLOR]: isNegativeMovement
    ? CHART_COLORS.DOWNTICK
    : CHART_COLORS.UPTICK,
  [CSS_VARIABLES.POINT_STROKE]: hasStuckTooltip
    ? 'var(--s-color-p-white-150)'
    : 'var(--s-color-p-white-170)',
  [CSS_VARIABLES.VALUATION_COLOR]: CHART_COLORS.VALUATION,
  [CSS_VARIABLES.PADDING]: `${CHART.PADDING}px`,
});

/**
 * Custom hook to manage chart styling and CSS variables
 * Centralizes all chart theming logic in one place
 */
export const useStyles = ({
  isNegativeMovement,
  isLoading,
  hasStuckTooltip,
}: UsePriceChartStylesProps) => {
  // Generate CSS custom properties for chart theming
  const chartCSSVariables = useMemo(
    () => getCssVariables({ isNegativeMovement, hasStuckTooltip }),
    [isNegativeMovement, hasStuckTooltip]
  );

  // Container styles with loading state opacity
  const containerStyles = useMemo(
    () => ({
      ...chartCSSVariables,
      opacity: isLoading ? CHART.LOADING_OPACITY : 1,
    }),
    [chartCSSVariables, isLoading]
  );

  return { chartCSSVariables, containerStyles };
};
